<script lang="ts">
  import { onMount } from "svelte";
  import echarts from "../../lib/echarts";
  import { mountChart, darkTooltip } from "../../lib/echartsHelpers";
  import type { DerivedWorkout } from "../../lib/lapAnalysis";
  import { fmtMSS } from "../../lib/format";
  import { strokeColor, contrastTextOn, COLORS } from "../../lib/designTokens";

  interface Props { dw: DerivedWorkout }
  let { dw }: Props = $props();

  let el: HTMLDivElement;

  // Sets with enough lengths to show as a labeled band (skip tiny 1-length anomalies)
  let labelSets = $derived(
    dw.setGroups.filter((s) => s.laps.length >= 2 && s.laps[0]?.strokeStyle !== "Unknown"),
  );

  function fmtDur(seconds: number): string {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    return fmtMSS(seconds);
  }

  onMount(() => {
    const hrData = dw.hrSeries.map((p) => [p.ts, p.bpm]);

    // Rest-interval shaded background bands — the "I took a break here" story.
    const restBands = dw.restIntervals.map((r) => [
      { xAxis: r.startMs, itemStyle: { color: "rgba(183, 199, 232, 0.10)" }, name: `${Math.round(r.durationSeconds)}s rest` },
      { xAxis: r.endMs },
    ]);

    // Stroke-set bands rendered as a thin colored strip ALONG THE TOP of the chart.
    // We use a separate ECharts series so each set is its own ribbon segment;
    // labels are rendered on the band itself.
    const stripY = 1;  // 0..1 fractional within the strip series
    const stripSeries = {
      type: "custom" as const,
      // tuple: [startMs, stripY, endMs, dotStroke (for color), label, meters]
      data: labelSets.map((s) => [s.startMs, stripY, s.endMs, s.laps[0].strokeStyle, s.label, s.meters]),
      renderItem: (_params: unknown, api: {
        coord: (pt: [number, number]) => [number, number];
        size: (range: [number, number]) => [number, number];
        value: (idx: number) => number | string;
        style: (opts: Record<string, unknown>) => Record<string, unknown>;
      }) => {
        const start = api.coord([api.value(0) as number, 0]);
        const end = api.coord([api.value(2) as number, 0]);
        const stroke = api.value(3) as string;
        const setLabel = api.value(4) as string;
        const meters = api.value(5) as number;
        const width = end[0] - start[0];
        const label = `${setLabel} ${meters}m`;
        const showLabel = width > 50;
        return {
          type: "group",
          children: [
            {
              type: "rect",
              shape: { x: start[0], y: 0, width: width, height: 16 },
              style: { fill: strokeColor(stroke), opacity: 0.85 },
            },
            ...(showLabel ? [{
              type: "text",
              style: {
                text: label,
                x: start[0] + width / 2,
                y: 8,
                textAlign: "center" as const,
                textVerticalAlign: "middle" as const,
                // Per-fill contrast: hardcoded navyDeep was near-black on the
                // freestyle midnight band — only backstroke yellow earns dark
                // text on this palette.
                fill: contrastTextOn(strokeColor(stroke)),
                font: "700 9px -apple-system, system-ui, sans-serif",
              },
            }] : [{ type: "text", style: { text: "", x: 0, y: 0 } }]),
          ],
        };
      },
    };

    return mountChart(el, (chart) => chart.setOption({
      backgroundColor: "transparent",
      grid: { left: 40, right: 12, top: 26, bottom: 28 },
      tooltip: {
        trigger: "axis",
        ...darkTooltip((params: { value: [number, number]; seriesIndex: number }[]) => {
          const hrPoint = params.find((p) => p.seriesIndex === 0);
          if (!hrPoint) return "";
          const d = new Date(hrPoint.value[0]);
          return `${d.toLocaleTimeString()}<br/><strong>${(hrPoint.value[1] as number).toFixed(0)} bpm</strong>`;
        }),
      },
      xAxis: {
        type: "time",
        axisLine: { lineStyle: { color: COLORS.navyRaised } },
        axisLabel: {
          color: COLORS.textDim,
          fontSize: 10,
          hideOverlap: true,
          formatter: (v: number) => {
            const d = new Date(v);
            return `${d.getHours()}:${String(d.getMinutes()).padStart(2, "0")}`;
          },
        },
        splitLine: { show: false },
      },
      yAxis: {
        type: "value",
        scale: true,
        axisLine: { show: false },
        axisLabel: { color: COLORS.textDim, fontSize: 10 },
        splitLine: { lineStyle: { color: COLORS.navyRaised, opacity: 0.3 } },
      },
      series: [
        {
          name: "Heart rate",
          type: "line",
          data: hrData,
          smooth: 0.3,
          showSymbol: false,
          lineStyle: { color: COLORS.coralRed, width: 2 },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: "rgba(255,107,107,0.30)" },
              { offset: 1, color: "rgba(255,107,107,0)" },
            ]),
          },
          markArea: {
            silent: true,
            // No in-chart labels — they collide when rests are close in time.
            // The phase list below the chart shows each rest's duration + HR
            // drop in a structured row, which is the right place for that detail.
            label: { show: false },
            itemStyle: { borderColor: "rgba(183,199,232,0.4)", borderWidth: 1, borderType: "dashed" as const },
            data: restBands,
          },
        },
        stripSeries,
      ],
    }));
  });
</script>

<article class="proto">
  <header>
    <span class="num">02</span>
    <h2>HR Story</h2>
    <p class="story">
      Heart rate over the swim. Rest intervals are auto-detected (HR &lt; 75% of avg) and shaded.
      The colored strip across the top labels stroke sets ≥ 50m.
    </p>
  </header>

  {#if dw.hrSeries.length === 0}
    <p class="muted">No heart rate samples in this workout.</p>
  {:else}
    <div class="chart" bind:this={el}></div>
    <div class="legend">
      <span class="stat"><strong>{dw.avgHr?.toFixed(0) ?? "—"}</strong> <span class="muted">avg</span></span>
      <span class="stat"><strong>{dw.maxHr?.toFixed(0) ?? "—"}</strong> <span class="muted">peak</span></span>
      <span class="stat rests">{dw.restIntervals.length} rest{dw.restIntervals.length === 1 ? "" : "s"}</span>
      <span class="stat sets">{labelSets.length} set{labelSets.length === 1 ? "" : "s"}</span>
    </div>
    {#if dw.phases.length > 0}
      <div class="phase-list">
        <div class="phase-title">Your workout, in phases:</div>
        {#each dw.phases as p, i (p.index)}
          {#if i > 0}
            {@const rest = dw.restIntervals.find((r) => Math.abs(r.endMs - p.startMs) < 2000)}
            {#if rest}
              <div class="phase-rest">⏸ rest {fmtDur(rest.durationSeconds)} · HR ↓ {Math.round(rest.minHr)}</div>
            {/if}
          {/if}
          <div class="phase-row">
            <span class="phase-label">{p.label}</span>
            <span class="phase-time">{new Date(p.startMs).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span>
            <span class="phase-stat">{p.lapEnd - p.lapStart + 1}× · {p.meters}m</span>
            <span class="phase-dur">{fmtDur(p.durationSeconds)}</span>
            {#if p.avgHr !== null}<span class="phase-hr">{p.avgHr.toFixed(0)} bpm</span>{/if}
          </div>
        {/each}
      </div>
    {/if}
  {/if}
</article>

<style>
  .proto { background: var(--bg-card); border-radius: 12px; padding: 1rem; margin: 0.75rem 0; }
  header { margin-bottom: 0.75rem; }
  .num { display: inline-block; padding: 0.1rem 0.5rem; background: var(--accent-fire); color: var(--bg-canvas); font-size: 0.7rem; font-weight: 700; border-radius: 3px; letter-spacing: 1px; }
  h2 { font-size: 1.1rem; font-weight: 700; margin: 0.4rem 0 0.2rem; }
  .story { font-size: 0.78rem; color: var(--text-muted); margin: 0; }
  .chart { width: 100%; height: 240px; }
  .legend { display: flex; gap: 0.75rem; margin-top: 0.5rem; font-size: 0.85rem; align-items: center; flex-wrap: wrap; }
  .stat strong { font-size: 1.1rem; color: var(--accent-fire); font-variant-numeric: tabular-nums; }
  .stat .muted { color: var(--text-muted); font-size: 0.75rem; margin-left: 0.2rem; }
  .stat.rests { color: var(--accent-primary); font-size: 0.78rem; font-weight: 600; }
  .stat.sets { color: var(--accent-primary); font-size: 0.78rem; font-weight: 600; }
  .muted { color: var(--text-muted); }

  .phase-list { margin-top: 0.75rem; }
  .phase-title { font-size: 0.78rem; color: var(--text-muted); margin-bottom: 0.4rem; text-transform: uppercase; letter-spacing: 1px; font-weight: 600; }
  .phase-row {
    display: grid;
    grid-template-columns: 1fr auto auto auto auto;
    gap: 0.5rem;
    align-items: center;
    padding: 0.4rem 0.5rem;
    background: var(--bg-canvas);
    border-radius: 4px;
    margin-bottom: 0.25rem;
    font-size: 0.78rem;
    font-variant-numeric: tabular-nums;
  }
  .phase-label { font-weight: 700; color: var(--accent-primary); }
  .phase-time { color: var(--text-muted); font-size: 0.72rem; }
  .phase-stat { color: var(--text-primary); }
  .phase-dur { color: var(--text-primary); font-weight: 600; }
  .phase-hr { color: var(--accent-fire); font-weight: 600; }
  .phase-rest {
    font-size: 0.72rem;
    color: var(--accent-primary);
    padding: 0.15rem 0.5rem;
    margin: 0.15rem 0;
    font-variant-numeric: tabular-nums;
  }
</style>
